import { useMemo } from "react";
import { useFeatureEntitlements } from "@frontegg/react";
import { APP_FEATURE_KEY } from "../utils/apps";

export type AppEntitlements = {
  /** The Spend app: Outpave-issued cards, which is what makes KYC/KYB necessary. */
  spend: boolean;
  /** The BYOC app: the customer's own cards, so no card issuer application and no KYC/KYB. */
  byoc: boolean;
  platform: boolean;
  /**
   * Whether the user reaches the Spend Management UI at all. Spend and BYOC are separate
   * app plans that share the same surface, so either one grants it — a BYOC-only company
   * gets the app without ever going through onboarding.
   */
  spendApp: boolean;
  /**
   * Whether the company reaches the Spend Management UI through BYOC alone. These companies
   * have no Outpave-issued cards, no wallet and no cash flow, so surfaces built around card
   * issuing need a BYOC variant rather than a hidden button here and there.
   */
  byocOnly: boolean;
  /** Entitlements keyed by app identity key, for the routing helpers in utils/apps. */
  features: Record<string, boolean>;
};

/**
 * Resolves the app-level (not add-on) entitlements of the logged-in user. Keeping the
 * app identity keys in one place stops each caller from having to know that BYOC is a
 * second way into the Spend Management app.
 */
export function useAppEntitlements(): AppEntitlements {
  const { isEntitled: spend } = useFeatureEntitlements(APP_FEATURE_KEY.SPEND);
  const { isEntitled: byoc } = useFeatureEntitlements(APP_FEATURE_KEY.BYOC);
  const { isEntitled: platform } = useFeatureEntitlements(
    APP_FEATURE_KEY.PLATFORM,
  );

  // Memoized so `features` keeps a stable identity across renders: callers pass it
  // straight into effect dependency arrays.
  return useMemo(
    () => ({
      spend,
      byoc,
      platform,
      spendApp: spend || byoc,
      byocOnly: byoc && !spend,
      features: {
        [APP_FEATURE_KEY.SPEND]: spend,
        [APP_FEATURE_KEY.BYOC]: byoc,
        [APP_FEATURE_KEY.PLATFORM]: platform,
      },
    }),
    [spend, byoc, platform],
  );
}

export default useAppEntitlements;
